import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { useOnboardVehicleOptions } from "@/hooks/useOnboardVehicleOptions";
import CreatableDropdownInput from "@/components/shared/CreatableDropdownInput";

const INITIAL_FORM = {
  vehicle_category: "EMERGENCY_VEHICLE",
  vin: "",
  registration_number: "",
  make: "",
  model: "",
  year: "",
  vehicle_type: "",
};

const AddVehicleModal = ({ open, onOpenChange, organizationId, onCreated }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const {
    makes,
    models,
    vehicleTypes,
    loading: optionsLoading,
  } = useOnboardVehicleOptions({ enabled: open });

  const makeOptions = Array.isArray(makes) ? makes : [];
  const modelOptions = Array.isArray(models) ? models : [];
  const vehicleTypeOptions = Array.isArray(vehicleTypes) ? vehicleTypes : [];

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(INITIAL_FORM);
  }, [open]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!organizationId) {
      setError("Organization is required to add a vehicle.");
      return;
    }

    const yearValue = Number(form.year);
    if (!Number.isFinite(yearValue) || yearValue <= 0) {
      setError("Please enter a valid year.");
      return;
    }

    const payload = {
      organization_id: organizationId,
      vehicle_category: "EMERGENCY_VEHICLE",
      vin: form.vin.trim(),
      registration_number: form.registration_number.trim(),
      make: form.make.trim(),
      model: form.model.trim(),
      year: yearValue,
      vehicle_type: form.vehicle_type.trim(),
    };

    const hasEmptyRequired = Object.entries(payload).some(([key, value]) => {
      if (key === "year") return false;
      return !value;
    });

    if (hasEmptyRequired) {
      setError("All fields are required.");
      return;
    }

    try {
      setSubmitting(true);
      await api.post("/vehicles", payload);
      toast.success("Vehicle added successfully.");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to add vehicle.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
   <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl rounded-2xl max-h-[90vh] overflow-y-auto">

        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Add Vehicle
          </DialogTitle>

          <DialogDescription>
            Fill in the vehicle details below.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label>Vehicle Category</Label>
              <select
                value={form.vehicle_category}
                disabled
                className="w-full h-10 rounded-md border border-gray-300 px-3 bg-white disabled:bg-slate-100 disabled:text-slate-600"
              >
                <option value="EMERGENCY_VEHICLE">EMERGENCY_VEHICLE</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label>VIN</Label>
              <Input
                value={form.vin}
                onChange={(e) => setField("vin", e.target.value)}
                placeholder="Vehicle Identification Number"
              />
            </div>

            <div className="space-y-2">
              <Label>Registration Number</Label>
              <Input
                value={form.registration_number}
                onChange={(e) => setField("registration_number", e.target.value)}
                placeholder="Registration Number"
              />
            </div>

             <div className="space-y-2">
              <Label>Year</Label>
              <select
                value={form.year}
                onChange={(e) => setField("year", e.target.value)}
                className="w-full h-10 rounded-md border border-gray-300 px-3 bg-white"
              >
                <option value="">Select Year</option>
                {Array.from({ length: 36 }, (_, idx) => String(1990 + idx)).reverse().map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Make</Label>
              <CreatableDropdownInput
                id="add-vehicle-make"
                placeholder="Select or type make"
                value={form.make}
                onChange={(value) => setField("make", value)}
                options={makeOptions}
                loading={optionsLoading}
                noOptionsText="No makes available. Type a custom make."
              />
            </div>

            <div className="space-y-2">
              <Label>Model</Label>
              <CreatableDropdownInput
                id="add-vehicle-model"
                placeholder="Select or type model"
                value={form.model}
                onChange={(value) => setField("model", value)}
                options={modelOptions}
                loading={optionsLoading}
                noOptionsText="No models available. Type a custom model."
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Vehicle Type</Label>
              <CreatableDropdownInput
                id="add-vehicle-type"
                placeholder="Select or type vehicle type"
                value={form.vehicle_type}
                onChange={(value) => setField("vehicle_type", value)}
                options={vehicleTypeOptions}
                loading={optionsLoading}
                noOptionsText="No vehicle types available. Type a custom vehicle type."
              />
            </div>
          </div>

          {error && (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>

            <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={submitting}>
              {submitting ? "Saving..." : "Save Vehicle"}
            </Button>
          </div>
        </form>

      </DialogContent>
    </Dialog>
  )
}

export default AddVehicleModal